import { COMPLETED_MAGIC_ID, VERIFIED_MAGIC_ID } from '@alltrails/shared/utils/lists/listHelpers';
import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import bboxPolygon from '@turf/bbox-polygon';
import pointInPolygon from '@turf/boolean-point-in-polygon';
import { Context } from '@alltrails/shared/types/context';
import { Filters } from 'types/Search';
import GeoLocation from 'types/Location/GeoLocation';
import { AlgoliaResults } from 'types/Search/Algolia';
import { Sort } from 'types/Search/Sort';
import { EXPLORE } from 'utils/constants/SearchAnalyticsConstants';
import logError from 'utils/logError';
import { getAnalyticsTags } from 'utils/requests/algolia_requests';
import { SearchFiltersUtil, CARD_ATTRIBUTES, SEARCH_INPUT_ATTRIBUTES } from 'utils/search_filters_util';
import { SearchResultsUtil } from './search_results_util';

const ET_PHOTO_USER_SLUG = 'alltrails-user';
const NUM_ALGOLIA_RESULTS = 50;

export const DEFAULT_RADIUS_METERS = 48270; // 30 miles in m

const algoliaAnalyticsTags = (context: Context, searchText: string, origin: string) =>
  getAnalyticsTags({
    user: context.currentUser,
    locale: context.languageRegionCode,
    origin,
    mobile: context.mobileBrowser,
    searchText,
    system: false
  });

export type SearchAppArgs = {
  algoliaIndex: any;
  atMaps: any;
  context: Context;
  filters: Filters;
  handlePlaceSearchResults?: any;
  handleSearchResults?: (results: AlgoliaResults) => void;
  handleUserResults?: any;
  initialUserSlug: string;
  insidePolygon?: number[][];
  isMobileWidth: boolean;
  isochroneBoundingBox?: [number, number, number, number];
  listMethods: any;
  page: string;
  geoLocation?: GeoLocation;
};

/**
 * legacyAlgoliaSearchAppQuery is our wrapper for Algolia search API calls in
 * /explore and its related code. This is prefixed as "legacy" because it has
 * been migrated from TS and is one of several disjoint methods for invoking
 * Algolia. A new strategy will emerge at some point and (ideally) we do not
 * enhance this method if possible.
 *
 * Conceptually, think of this method as if it were our own search API service
 * like /api/alltrails/search. This encapsulates all the functionality around
 * "here is my search state, give me back what I need to use the results"."
 *
 * @param {SearchAppArgs} args
 * @returns
 */
const legacyAlgoliaSearchAppQuery = async ({
  algoliaIndex,
  atMaps,
  context,
  filters,
  handlePlaceSearchResults,
  handleSearchResults,
  handleUserResults,
  initialUserSlug,
  insidePolygon,
  isMobileWidth,
  isochroneBoundingBox,
  listMethods,
  page
}: SearchAppArgs) => {
  // Sanity check that our index is ready to go!
  // Dont search on trail map page, or if quick-draw is enabled
  const isCertainPage = [
    PageStrings.EXPLORE_TRAIL_MAP_PAGE,
    PageStrings.EXPLORE_USERS_TRACKS_MAP_PAGE,
    PageStrings.EXPLORE_USERS_MAPS_MAP_PAGE,
    PageStrings.EXPLORE_PENDING_PAGE
  ].includes(page);
  if (!algoliaIndex || isCertainPage) {
    return;
  }

  if ([PageStrings.EXPLORE_USERS_TRACKS_PAGE, PageStrings.EXPLORE_USERS_MAPS_PAGE].includes(page)) {
    if (initialUserSlug === ET_PHOTO_USER_SLUG || !atMaps) {
      return;
    }

    handleUserResults(SearchFiltersUtil.customSearch(atMaps, filters, context, isMobileWidth));
    return;
  }

  // search for recordings or trails
  const completedIds = Object.keys(listMethods.itemsInListOfType(COMPLETED_MAGIC_ID, 'trail'));
  const verifiedIds = Object.keys(listMethods.itemsInListOfType(VERIFIED_MAGIC_ID, 'trail'));

  // TODO This approach may not be sustainable long term. See https://alltrails.atlassian.net/browse/DISCO-554
  if (filters.sort[Sort.NEWLY_ADDED]?.selected) {
    filters.has_profile_photo = true;
    filters.isMinimumViableContent = true;
  } else {
    filters.has_profile_photo = null;
    filters.isMinimumViableContent = null;
  }

  const algoliaQueryObject = SearchFiltersUtil.createAlgoliaQueryObject({
    insidePolygon,
    filters,
    mobileWidth: isMobileWidth,
    page,
    completedIds,
    verifiedIds,
    analyticsTags: algoliaAnalyticsTags(context, filters.queryTerm, EXPLORE),
    attributesToRetrieve: CARD_ATTRIBUTES,
    attributesToHighlight: filters.queryTerm && filters.queryTerm.length > 0 ? ['name'] : []
  });

  algoliaIndex
    .search(filters.queryTerm || '', algoliaQueryObject)
    .then((results: AlgoliaResults) => {
      let polygon: any;

      if (isochroneBoundingBox) {
        polygon = bboxPolygon(isochroneBoundingBox);
      } else if (filters.boundingBox) {
        polygon = bboxPolygon([
          filters.boundingBox.longitudeTopLeft,
          filters.boundingBox.latitudeBottomRight,
          filters.boundingBox.longitudeBottomRight,
          filters.boundingBox.latitudeTopLeft
        ]);
      }

      if (insidePolygon && polygon) {
        results.hits = results.hits?.filter(hit => {
          const point = [hit._geoloc.lng, hit._geoloc.lat];

          return pointInPolygon(point, polygon);
        });
      }

      handleSearchResults(results);
    })
    .catch(logError);

  // search for places (for admin filter)
  if (context.adminUser) {
    const algoliaAreaQueryObject = SearchFiltersUtil.createAlgoliaLocQueryObject(['area', 'place'], filters, isMobileWidth, page);
    algoliaIndex.search('', algoliaAreaQueryObject).then(handlePlaceSearchResults).catch(logError);
  }
};

export type SearchBoxArgs = {
  algoliaIndex: any;
  context: Context;
  enableClickTracking: boolean;
  filterType: string;
  latLng: [number, number] | null;
  origin: string;
  searchTerm: string;
  typeSubset: string[];
};

const normalizeAlgoliaResponse = (response: AlgoliaResults) =>
  new Promise(resolve => {
    response.hits = response.hits?.map(hit => {
      hit.filters = SearchResultsUtil.normalizeAlgoliaHitFilters(hit);
      return hit;
    });
    resolve(response);
  });

async function legacyAlgoliaSearchBoxQuery({
  algoliaIndex,
  context,
  enableClickTracking,
  filterType,
  latLng,
  origin,
  searchTerm,
  typeSubset
}: SearchBoxArgs): Promise<AlgoliaResults> {
  const analyticsTags = algoliaAnalyticsTags(context, searchTerm, origin);

  let facetFilters;
  if (filterType === 'all') {
    if (typeSubset?.length > 0) {
      // nested arrays -> OR;
      facetFilters = [typeSubset.filter(type => !['all', 'track', 'map'].includes(type)).map(type => `type:${type}`)];
    } else {
      facetFilters = ['type:-track', 'type:-map'];
    }
  } else {
    facetFilters = `type:${filterType}`;
  }

  const searchParams: any = {
    hitsPerPage: NUM_ALGOLIA_RESULTS,
    facets: ['type', 'difficulty_rating'],
    facetFilters,
    clickAnalytics: enableClickTracking,
    analyticsTags,
    attributesToRetrieve: SEARCH_INPUT_ATTRIBUTES
  };

  if (latLng?.length === 2) {
    searchParams.aroundLatLng = latLng.join(',');
    searchParams.aroundRadius = DEFAULT_RADIUS_METERS;

    return algoliaIndex.search('', searchParams).then(normalizeAlgoliaResponse);
  }

  searchParams.getRankingInfo = 1;
  searchParams.aroundLatLngViaIP = true;
  // rank uniformly based on geo in these bands: 0-50mi, 50-200mi, 200+
  searchParams.aroundPrecision = [
    { from: 0, value: 80000 },
    { from: 80000, value: 320000 },
    { from: 320000, value: 50000000 }
  ];
  searchParams.aroundRadius = 'all';

  return algoliaIndex.search(searchTerm, searchParams).then(normalizeAlgoliaResponse);
}

export { algoliaAnalyticsTags, legacyAlgoliaSearchAppQuery, legacyAlgoliaSearchBoxQuery };
